// Angular Core
import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injector,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withRouterConfig,
} from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { DebuggingService, LoadingService } from '@ic-monorepo/shared-utils';
import { TraceService } from '@sentry/angular';

// Environment
import { Environment } from '@ic-monorepo/shared-types';

// Firebase
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { Auth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { browserSessionPersistence, initializeAuth } from 'firebase/auth';

// Stores and services
import {
  DataAccessPatientUserSessionsService,
  DataAccessPatientUsersService,
} from '@ic-monorepo/data-access-firestore';
import { PatientSessionWatchService } from '@ic-monorepo/shared-auth-patient';
import {
  dialogServiceFactory,
  SessionStorageService,
  SubmissionAuditService,
  UUIDService,
  FileUploadService,
  PatientsService,
} from '@ic-monorepo/shared-services';
import { PatientUserStore } from '@ic-monorepo/shared-stores';
import {
  ClinicianSubmissionStore,
  MediaService,
  SubmissionFlowService,
  SubmissionFlowStore,
} from '@ic-monorepo/shared-submissions';
import { FormlyModule } from '@ngx-formly/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

// Internal - Services

// Internal - Pipes & Tokens
import { ENVIRONMENT, WINDOW, WINDOW_PROVIDER } from '@ic-monorepo/shared-injection-tokens';
import { CamelCaseToTitleCasePipe, StringToCapitalisedCasePipe } from '@ic-monorepo/shared-pipes';
import { PortalTheme } from '@ic-monorepo/ui-themes';

// App-specific
import { tap } from 'rxjs';
import { appRoutes } from './app.routes';
import { hydratePatientUserStore, resumeSessionWatch, subscribeToSessionExpiry } from './patient-session-initialiser';
import { PatientSubmissionErrorHandler } from './patient-submission-error.handler';

/**
 * Builds the application config from a resolved {@link Environment}.
 *
 * The environment is resolved before bootstrap (runtime `config.json` for the
 * production artefact, compiled environment otherwise) and threaded through here
 * so Firebase and the `ENVIRONMENT` token read the same source of truth.
 */
export function createAppConfig(environment: Environment): ApplicationConfig {
  return {
    providers: [
      provideHttpClient(withXhr()),
      provideAnimationsAsync(),
      provideRouter(
        appRoutes,
        withEnabledBlockingInitialNavigation(),
        withComponentInputBinding(),
        // Angular 22 defaults to 'always'; pin emptyOnly to preserve pre-v22 behaviour
        withRouterConfig({ paramsInheritanceStrategy: 'emptyOnly' }),
      ),

      providePrimeNG({
        theme: {
          preset: PortalTheme,
          options: {
            darkModeSelector: false,
            cssLayer: {
              name: 'primeng',
              order: 'tailwind-base, primeng, tailwind-components, tailwind-utilities',
            },
          },
        },
      }),

      provideFirebaseApp(() => initializeApp(environment.firebase)),
      // Use initializeAuth instead of getAuth to skip the iframe preload for
      // apis.google.com/js/api.js (REM-2056). The default getAuth() preloads a
      // popupRedirectResolver iframe that adds 5-24s to pageload. The patient app
      // only uses signInWithCustomToken (NHS login), so the resolver is not needed.
      provideAuth(() => {
        const app = getApp();
        return initializeAuth(app, {
          persistence: browserSessionPersistence,
        });
      }),
      provideFirestore(() => getFirestore()),
      provideStorage(() => getStorage()),
      provideFunctions(() => getFunctions(getApp(), environment.region)),
      provideAnalytics(() => getAnalytics()),
      provideRemoteConfig(() => getRemoteConfig()),

      importProvidersFrom(
        // Provide the SwUpdate DI token without registering a worker so the shared
        // ic-shell / SwUpdateService still resolve. enabled:false = no worker registered.
        ServiceWorkerModule.register('', { enabled: false }),
        FormlyModule.forRoot(),
      ),

      SubmissionFlowStore,
      ClinicianSubmissionStore,

      DialogService,
      DynamicDialogRef,
      DynamicDialogConfig,
      MessageService,
      ConfirmationService,

      FileUploadService,
      LoadingService,
      UUIDService,
      PatientsService,
      MediaService,
      SubmissionFlowService,

      StringToCapitalisedCasePipe,
      CamelCaseToTitleCasePipe,

      { provide: ENVIRONMENT, useValue: environment },
      { provide: ErrorHandler, useClass: PatientSubmissionErrorHandler },
      { provide: TraceService, deps: [Router] },
      WINDOW_PROVIDER,

      provideAppInitializer(() => {
        inject(TraceService);
      }),

      // Before anything new uploads: report items a killed, closed or reloaded tab left uploading
      provideAppInitializer(() => {
        inject(SubmissionAuditService).reportInterruptedUploads();
      }),

      provideAppInitializer(() => {
        const injector = inject(Injector);
        const dialogService = injector.get(DialogService);
        dialogServiceFactory(dialogService);
      }),

      // Patient session initialiser. Order matters:
      // 1. subscribeToSessionExpiry — wire up the expiry handler first so it's
      //    ready to catch already-expired sessions on resume
      // 2. hydratePatientUserStore — blocking; loads patient user from Firebase Auth
      // 3. resumeSessionWatch — starts the watch service if a session ID exists in
      //    sessionStorage (page refresh). Depends on both the expiry handler and
      //    the store being populated.
      provideAppInitializer(() => {
        const auth = inject(Auth);
        const router = inject(Router);
        const debug = inject(DebuggingService);
        const patientSessionWatchService = inject(PatientSessionWatchService);
        const dataAccessPatientUserSessionsService = inject(DataAccessPatientUserSessionsService);
        const dataAccessPatientUserService = inject(DataAccessPatientUsersService);
        const patientUserStore = inject(PatientUserStore);
        const submissionFlowStore = inject(SubmissionFlowStore);
        const sessionStorageService = inject(SessionStorageService);
        const environmentToken = inject(ENVIRONMENT);
        const windowRef = inject(WINDOW);

        subscribeToSessionExpiry(
          patientSessionWatchService,
          dataAccessPatientUserSessionsService,
          patientUserStore,
          submissionFlowStore,
          sessionStorageService,
          auth,
          router,
          environmentToken,
          windowRef,
          debug,
        );

        return hydratePatientUserStore(auth, dataAccessPatientUserService, patientUserStore, debug).pipe(
          tap(() => resumeSessionWatch(sessionStorageService, patientSessionWatchService, debug)),
        );
      }),
      provideZoneChangeDetection({ eventCoalescing: true }),
    ],
  };
}
